import React, { useState, useEffect } from "react";
import { ArrowRight, Menu, ChevronDown } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { content } from "../data/content";
import { servicesContent } from "../data/servicesContent";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "./ui/sheet";
import NarwhalMark from "./NarwhalMark";

const Navbar = ({ onBook }) => {
  const { lang, toggle } = useLanguage();
  const t = content[lang].nav;
  const services = servicesContent[lang].list;
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (id === "nosotros") {
      navigate("/nosotros");
      return;
    }
    if (id === "blogs") {
      navigate("/blog");
      return;
    }
    if (id === "faq") {
      navigate("/faq");
      return;
    }
    if (id === "glosario") {
      navigate("/glosario");
      return;
    }
    if (id === "descargables") {
      navigate("/descargables");
      return;
    }
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`/#${id}`);
    }
  };

  const goService = (slug) => {
    setOpen(false);
    navigate(slug ? `/servicios/${slug}` : "/servicios");
  };

  const goHome = () => {
    if (location.pathname === "/") window.scrollTo({ top: 0, behavior: "smooth" });
    else navigate("/");
  };

  return (
    <header className={`reference-header ${scrolled ? "is-scrolled" : ""}`} data-testid="site-header">
      <nav className="reference-nav" aria-label={t.menu} data-testid="primary-navigation">
        <button onClick={goHome} className="reference-brand" aria-label={t.home} data-testid="nav-home-button">
          <NarwhalMark testId="nav-brand-image" />
          <span data-testid="nav-brand-name">NARWHAL</span>
        </button>

        <div className="reference-nav-links">
          {t.links.filter((link) => link.id !== "modelo").map((l) =>
            l.dropdown ? (
              <div key={l.id} className="relative group" data-testid={`nav-${l.id}-dropdown`}>
                <button onClick={() => goSection(l.id)} className="inline-flex items-center gap-1" data-testid={`nav-link-${l.id}`}>
                  {l.label}<ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180" aria-hidden="true" />
                </button>
                <div className="invisible absolute left-1/2 top-full z-50 -translate-x-1/2 pt-4 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                  <div className="min-w-[220px] rounded-xl border border-white/10 bg-narwhal-panel/95 p-2 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.9)] backdrop-blur-xl">
                    {l.dropdown.map((item) => (
                      <button
                        key={item.label}
                        onClick={() => goSection(item.section)}
                        data-testid={`nav-${l.id}-${item.section}`}
                        className="block w-full rounded-lg px-3 py-2.5 text-left !text-sm !font-normal !text-white/80 !normal-case transition-colors duration-150 hover:!bg-white/5 hover:!text-narwhal-teal"
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : l.id === "servicios" ? (
              <div key={l.id} className="relative group" data-testid="nav-services-dropdown">
                <button onClick={() => goService()} className="inline-flex items-center gap-1" data-testid="nav-link-servicios">
                  {l.label}<ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180" aria-hidden="true" />
                </button>
                <div className="invisible absolute left-1/2 top-full z-50 -translate-x-1/2 pt-4 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                  <div className="min-w-[260px] rounded-xl border border-white/10 bg-narwhal-panel/95 p-2 shadow-[0_24px_60px_-30px_rgba(0,0,0,0.9)] backdrop-blur-xl">
                    <button
                      onClick={() => { setOpen(false); navigate("/direccion-comercial-externa"); }}
                      data-testid="nav-pillar-dce"
                      className="mb-1 block w-full rounded-lg px-3 py-2.5 text-left !text-sm !font-medium !text-narwhal-teal !normal-case transition-colors duration-150 hover:!bg-narwhal-teal/10"
                    >
                      {lang === "en" ? "External Commercial Direction" : "Dirección Comercial Externa"}
                    </button>
                    <div className="my-1 h-px bg-white/10" />
                    {services.map((s) => (
                      <button
                        key={s.slug}
                        onClick={() => goService(s.slug)}
                        data-testid={`nav-service-${s.slug}`}
                        className="block w-full rounded-lg px-3 py-2.5 text-left !text-sm !font-normal !text-white/80 !normal-case transition-colors duration-150 hover:!bg-white/5 hover:!text-narwhal-teal"
                      >
                        {s.shortName}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <button key={l.id} onClick={() => goSection(l.id)} data-testid={`nav-link-${l.id}`}>{l.label}</button>
            )
          )}
        </div>

        <div className="reference-nav-actions">
          <div className="reference-language" data-testid="nav-language-switch">
            <button onClick={() => lang !== "es" && toggle()} aria-label="Español" aria-pressed={lang === "es"} data-testid="nav-language-es">ES</button>
            <span aria-hidden="true">/</span>
            <button onClick={() => lang !== "en" && toggle()} aria-label="English" aria-pressed={lang === "en"} data-testid="nav-language-en">EN</button>
          </div>

          <button onClick={onBook} className="reference-nav-cta" data-testid="nav-book-call-button">{t.cta}<ArrowRight aria-hidden="true" /></button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="reference-menu-trigger" aria-label={t.menu} data-testid="mobile-menu-open-button"><Menu className="w-5 h-5" /></button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-narwhal-panel border-white/10 text-white w-[min(320px,100vw)] overflow-y-auto" aria-describedby={undefined} closeLabel={t.closeMenu} closeTestId="mobile-menu-close-button" data-testid="mobile-navigation-menu">
              <SheetTitle className="sr-only" data-testid="mobile-menu-title">{t.menu}</SheetTitle>
              <div className="flex flex-col gap-1 mt-10">
                {t.links.map((l) =>
                  l.dropdown ? (
                    <div key={l.id} className="border-b border-white/5 py-3">
                      <button onClick={() => goSection(l.id)} className="text-left text-lg text-white/80 hover:text-narwhal-teal transition-colors" data-testid={`mobile-nav-link-${l.id}`}>{l.label}</button>
                      <div className="mt-2 flex flex-col gap-1 pl-3">
                        {l.dropdown.map((item) => (
                          <button key={item.label} onClick={() => goSection(item.section)} className="text-left text-sm py-1.5 text-white/60 hover:text-narwhal-teal transition-colors" data-testid={`mobile-nav-${l.id}-${item.section}`}>{item.label}</button>
                        ))}
                      </div>
                    </div>
                  ) : l.id === "servicios" ? (
                    <div key={l.id} className="border-b border-white/5 py-3">
                      <button onClick={() => goService()} className="text-left text-lg text-white/80 hover:text-narwhal-teal transition-colors" data-testid="mobile-nav-link-servicios">{l.label}</button>
                      <div className="mt-2 flex flex-col gap-1 pl-3">
                        <button onClick={() => { setOpen(false); navigate("/direccion-comercial-externa"); }} className="text-left text-sm py-1.5 font-medium text-narwhal-teal transition-colors" data-testid="mobile-nav-pillar-dce">{lang === "en" ? "External Commercial Direction" : "Dirección Comercial Externa"}</button>
                        {services.map((s) => (
                          <button key={s.slug} onClick={() => goService(s.slug)} className="text-left text-sm py-1.5 text-white/60 hover:text-narwhal-teal transition-colors" data-testid={`mobile-nav-service-${s.slug}`}>{s.shortName}</button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <button key={l.id} onClick={() => goSection(l.id)} className="text-left text-lg py-3 border-b border-white/5 text-white/80 hover:text-narwhal-teal transition-colors" data-testid={`mobile-nav-link-${l.id}`}>{l.label}</button>
                  )
                )}
                <div className="flex items-center gap-2 mt-6">
                  <button onClick={() => lang !== "es" && toggle()} aria-label="Español" aria-pressed={lang === "es"} data-testid="mobile-menu-language-es" className={`px-4 py-2 rounded-md text-sm ${lang === "es" ? "bg-narwhal-teal text-narwhal-dark" : "border border-white/15 text-white/70"}`}>ES</button>
                  <button onClick={() => lang !== "en" && toggle()} aria-label="English" aria-pressed={lang === "en"} data-testid="mobile-menu-language-en" className={`px-4 py-2 rounded-md text-sm ${lang === "en" ? "bg-narwhal-teal text-narwhal-dark" : "border border-white/15 text-white/70"}`}>EN</button>
                </div>
                <button onClick={() => { setOpen(false); onBook(); }} className="mt-6 rounded-md bg-narwhal-teal text-narwhal-dark font-semibold px-4 py-3" data-testid="mobile-menu-book-call-button">{t.cta}</button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
